import { FC, ReactElement } from 'react'
import styled from 'styled-components'
import useImageFallback from 'hooks/useImageFallback'
import { MdFavorite } from 'react-icons/md'
import { TfiLoop } from 'react-icons/tfi'
import { device } from '../../breakpoints'
import Rating from 'components/Rating'
import { useTheme } from 'styled-components'

interface ListItemProps {
  id: string
  metadataId: string
  type: string
  titleHeader: string
  title: string
  short: string
  videoId: string
  imageUrl: string
  description: string
  favorite: boolean
  highlighted: boolean
  startDate: string
  links: any
  trailerUrl: string
  alreadySeen: boolean
  authorRating: number
  providers: any
  // handleClick: (id: string) => void
}

const ListItem: FC<ListItemProps> = (props): ReactElement => {
  const theme = useTheme()
  const imageUrl = useImageFallback(
    props.imageUrl,
    require('./imageFallback.jpg')
  )

  return (
    <Container
      target='_blank'
      href={
        process.env.NODE_ENV !== 'production'
          ? `https://master.dev.${theme.tenant.key}.ch/${props.metadataId}`
          : `https://www.${theme.tenant.key}.ch/${props.metadataId}`
      }
    >
      <Content>
        <ImageContainer imageUrl={imageUrl} />
        <Type>{props.titleHeader}</Type>
        <Title>{props.title}</Title>
        {props.alreadySeen && (
          <Again>
            <TfiLoop /> À revoir
          </Again>
        )}
        <Description>{props.short}</Description>
        <ReadItLink>Lire la suite</ReadItLink>
      </Content>
      <Rating id={props.metadataId} authorRating={props.authorRating} />
      {props.favorite && (
        <Favorite>
          <FavoriteContent>
            <MdFavorite />
          </FavoriteContent>
        </Favorite>
      )}
    </Container>
  )
}

const ReadItLink = styled.div`
  position: relative;
  display: inline-block;
  font-family: var(--font-plex);
  color: var(--tenant-${({ theme }) => theme.tenant.key.slice(0, 3)});
  font-weight: 400;
  font-size: 1.5rem;
  line-height: 2.1rem;
  margin-bottom: 1rem;

  &:after {
    content: '';
    width: 0%;
    background: var(--tenant-${({ theme }) => theme.tenant.key.slice(0, 3)});
    height: 3px;
    display: block;
    transform: scaleZ(0);
    transition: width 0.3s ease-in-out;
  }
`

const Container = styled.a`
  font-family: var(--font-noto);
  color: ${({ theme }) => theme.colorText};
  font-size: 1.8rem;
  line-height: 3rem;
  cursor: pointer;
  position: relative;
  background: var(--site-background);

  text-decoration: none;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding: 15px 0;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding: 20px;
  }
`

const Again = styled.div`
  font-size: 1.25rem;
  font-family: var(--font-plex);
  font-weight: 700;
  display: inline-block;
  line-height: 0;
  padding: 0.5rem 1rem 0.85rem 1rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.secondaryButtonBackground};
`

const Content = styled.div``

const ImageContainer = styled.div.attrs<{ imageUrl: string }>(
  ({ imageUrl }) => ({
    style: {
      backgroundImage: `url(${imageUrl})`,
    },
  })
)<{ imageUrl: string }>`
  height: 0;
  overflow: hidden;
  padding-top: 56.25%;
  position: relative;
  margin-bottom: 1em;
  background-size: cover;
  background-position: center;
`

const Type = styled.div`
  font-family: var(--font-plex);
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.4;
  color: var(--tenant-${({ theme }) => theme.tenant.key.slice(0, 3)});
  -webkit-font-smoothing: antialiased;
`

const Title = styled.div`
  font-family: var(--font-sole);
  font-weight: 700;
  line-height: 1.4;
  -webkit-font-smoothing: antialiased;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    font-size: 2rem;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    font-size: 2.1rem;
  }
`

const Description = styled.div`
  position: relative;
  margin-top: 1rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colorText};
  font-weight: 400;
  font-size: 1.5rem;
  font-family: var(--font-plex);
  line-height: 2.1rem;
  height: 150px;
  overflow: hidden;
  background: var(--site-background);
  margin-bottom: -40px;

  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    background: var(--site-background);
    width: 100%;
    height: 200px;
    z-index: 0;
    mask-image: linear-gradient(0deg, rgba(0, 0, 0, 1) 20%, rgba(0, 0, 0, 0));
  }
`

const Favorite = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: var(--tenant-${({ theme }) => theme.tenant.key.slice(0, 3)});
  color: #ffffff;
  padding: 1.15rem 1rem 0.5rem 1rem;
  font-size: 3rem;
  width: 10%;

  &:after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
`

const FavoriteContent = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
`

export default ListItem
